import React, { useEffect, useRef, useState } from "react";
import { Col } from "antd";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import { AttendanceStatusActionCreators } from "redux/rootActions";
import {
  attendanceStatusDataResponse,
  selectIsLoading,
} from "redux/dashboard/attendanceStatus/attendanceStatusStates";

import { Loading } from "components";
import { DownloadIcon, CloseIcon, NoDataIcon } from "icons";
import { generateImg } from "utils";

import {
  BoxItem,
  BoxHeader,
  BoxTitle,
  LightDivider,
  BoxContent,
  WrapperLegendLineChart,
  Dot,
  WrapperChart,
  ContainerChart,
  CheckboxStyled2,
  WrapperLoadingChart,
  WrapperCloseIcon,
  WrapperNoData,
} from "../Dashboard.style";

interface TooltipItem {
  dot: any;
  label: string;
}

interface BarChartProps {
  data: any;
  checkedList: boolean[];
}

const chartItems = [
  {
    id: 0,
    label: "attendance-label",
    color: "#2AC769",
  },
  {
    id: 1,
    label: "absence-label",
    color: "#FB4E4E",
  },
  {
    id: 2,
    label: "behind-time",
    color: "#F49506",
  },
  {
    id: 3,
    label: "leave-early",
    color: "#0A88CE",
  },
  {
    id: 4,
    label: "online",
    color: "#C72AB7",
  },
  {
    id: 5,
    label: "holiday",
    color: "#83BCE2",
  },
  {
    id: 6,
    label: "not-implement",
    color: "#666666",
  },
];

const returnItem = (name: string) => {
  let item: TooltipItem = {
    dot: <></>,
    label: "",
  };
  switch (name) {
    case "attendance":
      item = {
        dot: <Dot style={{ background: "#2AC769" }} />,
        label: "attendance-label",
      };
      break;
    case "absence":
      item = {
        dot: <Dot style={{ background: "#FB4E4E" }} />,
        label: "absence-label",
      };
      break;
    case "behindTime":
      item = {
        dot: <Dot style={{ background: "#F49506" }} />,
        label: "behind-time",
      };
      break;
    case "leaveEarly":
      item = {
        dot: <Dot style={{ background: "#0A88CE" }} />,
        label: "leave-early",
      };
      break;
    case "online":
      item = {
        dot: <Dot style={{ background: "#C72AB7" }} />,
        label: "online",
      };
      break;
    case "holiday":
      item = {
        dot: <Dot style={{ background: "#83BCE2" }} />,
        label: "holiday",
      };
      break;
    case "notImplement":
      item = {
        dot: <Dot style={{ background: "#666666" }} />,
        label: "not-implement",
      };
      break;
  }

  return item;
};

const CustomizedXAxisTick = (props: any) => {
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-10} y={0} dy={18} fill="#3B3B3B">
        {payload.value}
      </text>
    </g>
  );
};

const CustomizedYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "body-temperature",
  });
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={3} fill="#3B3B3B" textAnchor="end">
        {payload.value} {t("people")}
      </text>
    </g>
  );
};

const BarChartCustom = ({ data, checkedList }: BarChartProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "body-temperature",
  });
  const { t: t1 } = useTranslation("translation", {
    keyPrefix: "dashboard",
  });

  const renderTooltipContent = (props: any) => {
    const { payload, label } = props;

    return (
      <div className="customized-tooltip-content">
        <p className="label">{label}</p>
        {payload && (
          <div className="list">
            {payload.map((entry: any, index: any) => (
              <div key={`item-${index}`} className="tooltip-item-wrapper">
                <div className="item-left">
                  {returnItem(entry.name)?.dot}
                  <span>{t1(returnItem(entry.name)?.label)}</span>
                </div>
                <div className="item-right">
                  <span>
                    {entry.value}
                    {t("people")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <ContainerChart>
      <ResponsiveContainer width="100%" height={178}>
        <BarChart
          margin={{
            top: 10,
            right: 20,
            left: 0,
            bottom: 0,
          }}
          data={data}
        >
          <CartesianGrid
            vertical={false}
            strokeDasharray="2 2"
            stroke="#CCCCCC"
          />
          <XAxis
            dy={4}
            tickLine={false}
            dataKey="date"
            minTickGap={-100}
            tick={<CustomizedXAxisTick />}
            stroke="#CCCCCC"
          />
          <YAxis
            type="number"
            tickLine={false}
            interval={0}
            width={55}
            tick={<CustomizedYAxisTick />}
            stroke="#CCCCCC"
            minTickGap={200}
          />
          {checkedList[0] && (
            <Bar
              dataKey="attendance"
              barSize={44}
              stackId="noHeat"
              fill="#2AC769"
            />
          )}
          {checkedList[1] && (
            <Bar
              dataKey="absence"
              stackId="noHeat"
              fill="#FB4E4E"
              barSize={44}
            />
          )}
          {checkedList[2] && (
            <Bar
              dataKey="behindTime"
              barSize={44}
              stackId="noHeat"
              fill="#F49506"
            />
          )}
          {checkedList[3] && (
            <Bar
              dataKey="leaveEarly"
              barSize={44}
              stackId="noHeat"
              fill="#0A88CE"
            />
          )}
          {checkedList[4] && (
            <Bar
              dataKey="online"
              barSize={44}
              stackId="noHeat"
              fill="#C72AB7"
            />
          )}
          {checkedList[5] && (
            <Bar
              dataKey="holiday"
              barSize={44}
              stackId="noHeat"
              fill="#83BCE2"
            />
          )}
          {checkedList[6] && (
            <Bar
              dataKey="notImplement"
              barSize={44}
              stackId="noHeat"
              fill="#666666"
            />
          )}
          <Tooltip content={renderTooltipContent} />
        </BarChart>
      </ResponsiveContainer>
    </ContainerChart>
  );
};

const AttendanceStatus = ({ handleCloseChart }: any) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard",
  });

  const dispatch = useDispatch();
  const attendanceStatusData = useSelector(attendanceStatusDataResponse);
  const isLoading = useSelector(selectIsLoading);

  const imgRef = useRef<any>();
  const [checkedList, setCheckedList] = useState<boolean[]>([
    true,
    true,
    true,
    true,
    true,
    true,
    true,
  ]);
  const [data, setData] = useState([]);

  const onChangeCheckbox = (index: any) => {
    const tempCheckedList = [...checkedList];
    tempCheckedList[index] = !tempCheckedList[index];
    setCheckedList(tempCheckedList);
  };

  const handleConvertDate = (date: string) => {
    return moment(new Date(date)).format("M/D");
  };

  useEffect(() => {
    dispatch(
      AttendanceStatusActionCreators.getAttendanceStatusAction({
        aggregate_date_to: moment(new Date()).format("YYYY-MM-DD"),
      })
    );
  }, []);

  useEffect(() => {
    if (attendanceStatusData && Object.keys(attendanceStatusData).length) {
      setData(
        attendanceStatusData.attendance_status.map((item: any) => {
          return {
            date: handleConvertDate(item.aggregate_date),
            absence: item.amounts.absent,
            behindTime: item.amounts.tardy,
            leaveEarly: item.amounts.early,
            online: item.amounts.online,
            holiday: item.amounts.holiday,
            attendance: item.amounts.attend,
            notImplement: item.amounts.not_answered,
          };
        })
      );
    }
  }, [attendanceStatusData]);

  return (
    // <Col xs={24} md={24} xl={12} >
    <BoxItem ref={imgRef}>
      <WrapperCloseIcon>
        <CloseIcon
          fill="#2F8CAE"
          height="14px"
          width="14px"
          onClick={() => handleCloseChart("1")}
        />
      </WrapperCloseIcon>
      <BoxHeader>
        <BoxTitle>{t("attendance-status-label")}</BoxTitle>
        <DownloadIcon
          fill="#3B3B3B"
          style={{ marginTop: 4, cursor: "pointer" }}
          onClick={(event: any) => {
            if (!event.detail || event.detail == 1) {
              generateImg("attendance-status", imgRef.current);
            }
          }}
        />
      </BoxHeader>
      <LightDivider />
      {isLoading ? (
        <WrapperLoadingChart>
          <div className="container">
            <Loading width={70} height={70} />
          </div>
        </WrapperLoadingChart>
      ) : data.length > 0 ? (
        <BoxContent>
          <WrapperChart>
            <ContainerChart>
              <div className="icon-descriptions">
                {chartItems && (
                  <div
                    className="icon-descriptions-stacked"
                    style={{ display: "flex", height: "max-content" }}
                  >
                    {chartItems.map((item: any) => (
                      <div className="description" key={item.id}>
                        <CheckboxStyled2
                          checked={checkedList[item.id]}
                          onChange={() => onChangeCheckbox(item.id)}
                        />
                        <WrapperLegendLineChart>
                          <Dot
                            style={{ background: item.color, marginTop: 0 }}
                          />
                          <p>{t(item.label)}</p>
                        </WrapperLegendLineChart>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ContainerChart>
            <BarChartCustom data={data} checkedList={checkedList} />
          </WrapperChart>
        </BoxContent>
      ) : (
        <WrapperNoData>
          <NoDataIcon width={140} height={140} />
        </WrapperNoData>
      )}
    </BoxItem>
    // </Col>
  );
};

export default AttendanceStatus;
